<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import {
  getMfaStatus,
  setupMfa,
  enableMfa,
  disableMfa,
} from 'dashboard/api/profileMfa';

const { t } = useI18n();

const status = ref({ enabled: false, enabled_at: null });
const isLoadingStatus = ref(true);

const showEnableModal = ref(false);
const showDisableModal = ref(false);

const enableStep = ref('password'); // 'password' | 'scan' | 'otp'
const enablePassword = ref('');
const enableOtp = ref('');
const qrCodeSvg = ref('');
const provisioningSecret = ref('');
const isProcessing = ref(false);

const disablePassword = ref('');

const inputStyles = {
  borderRadius: '0.375rem',
  padding: '0.375rem 0.75rem',
  fontSize: '0.875rem',
  marginBottom: '0.125rem',
};

const fetchStatus = async () => {
  try {
    const { data } = await getMfaStatus();
    status.value = data.data;
  } catch (error) {
    status.value = { enabled: false, enabled_at: null };
  } finally {
    isLoadingStatus.value = false;
  }
};

const formattedEnabledAt = () =>
  status.value.enabled_at
    ? new Date(status.value.enabled_at).toLocaleDateString('pt-BR')
    : '';

const resetEnableState = () => {
  enableStep.value = 'password';
  enablePassword.value = '';
  enableOtp.value = '';
  qrCodeSvg.value = '';
  provisioningSecret.value = '';
};

const openEnableModal = () => {
  resetEnableState();
  showEnableModal.value = true;
};

const closeEnableModal = () => {
  showEnableModal.value = false;
};

const openDisableModal = () => {
  disablePassword.value = '';
  showDisableModal.value = true;
};

const closeDisableModal = () => {
  showDisableModal.value = false;
};

const submitEnablePassword = async () => {
  if (!enablePassword.value) return;
  isProcessing.value = true;
  try {
    const { data } = await setupMfa(enablePassword.value);
    qrCodeSvg.value = data.data.qr_code_svg;
    provisioningSecret.value = data.data.secret;
    enableStep.value = data.data.already_enrolled ? 'otp' : 'scan';
  } catch (error) {
    const message =
      error?.response?.status === 401
        ? t('PROFILE_SETTINGS.FORM.MFA_SECTION.INVALID_PASSWORD')
        : t('PROFILE_SETTINGS.FORM.MFA_SECTION.GENERIC_ERROR');
    useAlert(message);
  } finally {
    isProcessing.value = false;
  }
};

const submitEnableOtp = async () => {
  if (!enableOtp.value) return;
  isProcessing.value = true;
  try {
    const { data } = await enableMfa(enableOtp.value);
    status.value = data.data;
    useAlert(t('PROFILE_SETTINGS.FORM.MFA_SECTION.ENABLE_SUCCESS'));
    closeEnableModal();
  } catch (error) {
    const message =
      error?.response?.status === 422
        ? t('PROFILE_SETTINGS.FORM.MFA_SECTION.INVALID_OTP')
        : t('PROFILE_SETTINGS.FORM.MFA_SECTION.GENERIC_ERROR');
    useAlert(message);
  } finally {
    isProcessing.value = false;
  }
};

const submitDisable = async () => {
  if (!disablePassword.value) return;
  isProcessing.value = true;
  try {
    const { data } = await disableMfa(disablePassword.value);
    status.value = data.data;
    useAlert(t('PROFILE_SETTINGS.FORM.MFA_SECTION.DISABLE_SUCCESS'));
    closeDisableModal();
  } catch (error) {
    const message =
      error?.response?.status === 401
        ? t('PROFILE_SETTINGS.FORM.MFA_SECTION.INVALID_PASSWORD')
        : t('PROFILE_SETTINGS.FORM.MFA_SECTION.GENERIC_ERROR');
    useAlert(message);
  } finally {
    isProcessing.value = false;
  }
};

onMounted(fetchStatus);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="isLoadingStatus" class="flex py-2">
      <Spinner color-scheme="primary" size="" />
    </div>
    <template v-else>
      <div class="flex items-center gap-2">
        <span
          v-if="status.enabled"
          class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
        >
          {{
            $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STATUS_ENABLED_AT', {
              date: formattedEnabledAt(),
            })
          }}
        </span>
        <span
          v-else
          class="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-500/20"
        >
          {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STATUS_DISABLED') }}
        </span>
      </div>
      <div>
        <NextButton
          v-if="!status.enabled"
          :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.ENABLE')"
          @click="openEnableModal"
        />
        <NextButton
          v-else
          ruby
          faded
          :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.DISABLE')"
          @click="openDisableModal"
        />
      </div>
    </template>

    <woot-modal v-model:show="showEnableModal" :on-close="closeEnableModal">
      <div class="p-8 flex flex-col gap-5 min-w-[420px]">
        <template v-if="enableStep === 'password'">
          <h2 class="text-lg font-semibold">
            {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_PASSWORD_TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_PASSWORD_HINT') }}
          </p>
          <form @submit.prevent="submitEnablePassword">
            <woot-input
              v-model="enablePassword"
              type="password"
              :styles="inputStyles"
              :label="
                $t('PROFILE_SETTINGS.FORM.MFA_SECTION.CURRENT_PASSWORD_LABEL')
              "
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.MFA_SECTION.CURRENT_PASSWORD_PLACEHOLDER'
                )
              "
            />
            <div class="flex justify-end gap-2 mt-4">
              <NextButton
                slate
                faded
                :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.BTN_CANCEL')"
                @click="closeEnableModal"
              />
              <NextButton
                type="submit"
                :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.BTN_CONTINUE')"
                :disabled="!enablePassword || isProcessing"
                :is-loading="isProcessing"
              />
            </div>
          </form>
        </template>
        <template v-else>
          <template v-if="enableStep === 'scan'">
            <h2 class="text-lg font-semibold">
              {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_QR_TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_QR_HINT') }}
            </p>
            <div class="flex justify-center">
              <div class="bg-white p-3 rounded-md" v-html="qrCodeSvg" />
            </div>
            <code
              class="text-xs bg-n-slate-3 dark:bg-n-solid-2 px-2 py-1 rounded select-all break-all text-center"
            >
              {{ provisioningSecret }}
            </code>
          </template>
          <h3 class="text-base font-semibold pt-2">
            {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_OTP_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_OTP_HINT') }}
          </p>
          <form @submit.prevent="submitEnableOtp">
            <woot-input
              v-model="enableOtp"
              :styles="inputStyles"
              :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.OTP_LABEL')"
              :placeholder="
                $t('PROFILE_SETTINGS.FORM.MFA_SECTION.OTP_PLACEHOLDER')
              "
            />
            <div class="flex justify-end gap-2 mt-4">
              <NextButton
                slate
                faded
                :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.BTN_CANCEL')"
                @click="closeEnableModal"
              />
              <NextButton
                type="submit"
                :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.BTN_ACTIVATE')"
                :disabled="!enableOtp || isProcessing"
                :is-loading="isProcessing"
              />
            </div>
          </form>
        </template>
      </div>
    </woot-modal>

    <woot-modal v-model:show="showDisableModal" :on-close="closeDisableModal">
      <div class="p-8 flex flex-col gap-5 min-w-[420px]">
        <h2 class="text-lg font-semibold">
          {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_DISABLE_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('PROFILE_SETTINGS.FORM.MFA_SECTION.STEP_DISABLE_HINT') }}
        </p>
        <form @submit.prevent="submitDisable">
          <woot-input
            v-model="disablePassword"
            type="password"
            :styles="inputStyles"
            :label="
              $t('PROFILE_SETTINGS.FORM.MFA_SECTION.CURRENT_PASSWORD_LABEL')
            "
            :placeholder="
              $t(
                'PROFILE_SETTINGS.FORM.MFA_SECTION.CURRENT_PASSWORD_PLACEHOLDER'
              )
            "
          />
          <div class="flex justify-end gap-2 mt-4">
            <NextButton
              slate
              faded
              :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.BTN_CANCEL')"
              @click="closeDisableModal"
            />
            <NextButton
              ruby
              type="submit"
              :label="$t('PROFILE_SETTINGS.FORM.MFA_SECTION.BTN_DISABLE')"
              :disabled="!disablePassword || isProcessing"
              :is-loading="isProcessing"
            />
          </div>
        </form>
      </div>
    </woot-modal>
  </div>
</template>
